import { formatShortDate } from '@packages/format';
import { asOfSeriesColumns, mergeSeriesAccounts, mergeSeriesRows, } from './series-presentation.js';
export function trustCreditorsSeriesColumns(series) {
    return asOfSeriesColumns(series.columns, formatShortDate);
}
/**
 * The three sections, their totals, and the per-date reconciliation difference.
 *
 * Creditor rows are keyed by `clientId`, not by a ledger account - the creditors
 * section is a per-client breakdown of ONE control account, so account identity
 * would collapse every client into a single row. They are ordered by client
 * name, which is how the single-date report reads and the only order a person
 * scanning for a client can use.
 *
 * A client absent from a column held nothing in trust on that date and shows
 * 0.00 there, not a blank. That is not a display nicety: on a trust
 * reconciliation "we held nothing for this client on that date" is a positive
 * assertion, and rendering it as an absence would let a reader take it for
 * missing data.
 */
export function buildTrustCreditorsSeriesTableRows(columns) {
    const assetTotals = columns.map((column) => column.statement.assetsTotalMinorUnits);
    const creditorTotals = columns.map((column) => column.statement.creditorsTotalMinorUnits);
    const otherLiabilityTotals = columns.map((column) => column.statement.otherLiabilitiesTotalMinorUnits);
    const liabilityTotals = creditorTotals.map((creditors, index) => creditors + otherLiabilityTotals[index]);
    return [
        {
            kind: 'assets',
            label: 'Trust assets',
            rows: mergeSeriesAccounts(columns.map((column) => column.statement.assets)),
            subtotalMinorUnits: assetTotals,
        },
        {
            kind: 'creditors',
            label: 'Trust creditors',
            rows: mergeSeriesRows(columns.map((column) => column.statement.creditors), (creditor) => creditor.clientId, (a, b) => a.clientName.localeCompare(b.clientName)),
            subtotalMinorUnits: creditorTotals,
        },
        {
            kind: 'otherLiabilities',
            label: 'Other trust liabilities',
            rows: mergeSeriesAccounts(columns.map((column) => column.statement.otherLiabilities)),
            subtotalMinorUnits: otherLiabilityTotals,
        },
        { kind: 'total', label: 'Total trust liabilities', amountsMinorUnits: liabilityTotals },
        {
            kind: 'reconciliation',
            label: 'Difference',
            amountsMinorUnits: assetTotals.map((assets, index) => assets - liabilityTotals[index]),
        },
    ];
}
/**
 * The column indexes where trust assets did not cover trust liabilities.
 *
 * Reported rather than thrown, for the reason the whole reconciliation exists:
 * a trust shortfall is a fact about the books, and a report that refused to
 * render it would suppress the one signal a practice and its auditor need.
 */
export function unreconciledTrustCreditorsSeriesColumns(columns) {
    return columns.flatMap((column, index) => column.statement.assetsTotalMinorUnits ===
        column.statement.creditorsTotalMinorUnits + column.statement.otherLiabilitiesTotalMinorUnits
        ? []
        : [index]);
}
